// Régie du direct (feat/regie, spec zgNZThhO PR C) — demande Luc du 31/10 :
// « même quelqu'un qui est animateur doit voir la même chose que les
// participants […] on a un autre onglet sur lequel on peut aller lancer
// une question, mettre un truc en plein écran […] démarrer et arrêter les
// enregistrements ».
//
// La salle de l'animation est celle des participants ; les commandes sont
// ici, dans un onglet à part (2e écran, tablette). La régie NE REJOINT PAS
// la salle LiveKit : aucun flux vidéo, aucune connexion média, aucun hook
// qui exigerait un RoomContext. Tout passe par Rails :
//   - /regie/status (5 s) : état du direct, présents, scène, mise en avant
//     (lus par le serveur dans LiveKit, RoomSnapshot) ;
//   - la modération (feat/regie-moderation) : couper le micro d'un
//     intervenant ou d'un spectateur, ou le retirer du direct
//     (WebinarParticipantsController) ;
//   - les endpoints existants des composants réutilisés (StateButton,
//     RecordingControl, HandRaiseHost, mise en avant, DiscussionPanel), qui
//     revérifient le rôle d'animation à chaque appel ;
//   - les consoles autonomes des sondages (PollConsole, son propre chrono
//     lu sur le plan serveur, présents fournis par /regie/status) et des
//     questions du public (QaConsole). « Ajouter à la file » depuis la
//     discussion crée une question manuelle et relit aussitôt la file.
//
// Fail-loud : une salle illisible ou un refus s'affichent, jamais devinés.

import { createRoot } from "react-dom/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { StateButton, csrfToken } from "./state_button";
import { useTwoStepConfirm } from "./two_step_confirm";
import { RecordingControl } from "./recording_control";
import { HandRaiseHost } from "./hand_raise_host";
import { useSpotlightControl } from "./spotlight";
import { DiscussionPanel, useChat } from "./chat";
import { PollConsole } from "./poll_console";
import { QaConsole } from "./qa_console";

const MOUNT_ID = "regie";
const STATUS_POLL_MS = 5000;
// Onglet masqué (la salle au premier plan, régie derrière) : la page n'est
// pas lue, on relit rarement ; au retour, relecture immédiate.
const HIDDEN_STATUS_POLL_MS = 30000;
const STATE_LABELS = { draft: "Hors antenne", live: "En direct", ended: "Terminé" };
const STATE_BADGES = { draft: "etat--neutre", live: "etat--en-cours", ended: "etat--neutre" };

async function readStatus(url) {
  const res = await fetch(url, {
    credentials: "same-origin",
    cache: "no-store",
    redirect: "manual",
    headers: { Accept: "application/json" },
  });
  if (res.type === "opaqueredirect") throw new Error("session animateur expirée, reconnectez-vous");
  if (!res.ok) throw new Error(`serveur ${res.status}`);
  return res.json();
}

// État de la régie relu sur le serveur, toutes les STATUS_POLL_MS (onglet
// visible) ou HIDDEN_STATUS_POLL_MS (onglet masqué). `refresh` force une
// relecture (après une commande, pour ne pas attendre le prochain tour).
function useRegieStatus(statusUrl) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const refresh = useCallback(async () => {
    try {
      const payload = await readStatus(statusUrl);
      if (!mountedRef.current) return;
      setStatus(payload);
      setError(null);
    } catch (err) {
      if (mountedRef.current) setError(err.message || "réseau");
    }
  }, [statusUrl]);

  useEffect(() => {
    let timer = null;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(tick, document.hidden ? HIDDEN_STATUS_POLL_MS : STATUS_POLL_MS);
    };
    const tick = () => {
      refresh();
      schedule();
    };
    const onVisibilityChange = () => (document.hidden ? schedule() : tick());
    tick();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refresh]);

  return { status, error, refresh };
}

function useNow(running) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);
  return now;
}

function formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const seconds = String(total % 60).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

// Chrono depuis le premier démarrage du direct (`started_at`, conservé à la
// reprise). Rien à compter hors direct.
function LiveClock({ state, liveSince }) {
  const running = state === "live" && !!liveSince;
  const now = useNow(running);
  if (!running) return null;
  return (
    <span className="regie-entete__chrono" data-testid="regie-clock" aria-label="Temps écoulé depuis le début du direct">
      {formatElapsed(now - Date.parse(liveSince))}
    </span>
  );
}

function presentLabel(status) {
  if (!status) return "Salle : lecture…";
  if (status.room === "unavailable") return "Salle LiveKit injoignable";
  if (status.room === "closed") return "Salle vide";
  return `${status.present} présent${status.present > 1 ? "s" : ""}`;
}

function RegieHeader({ state, liveSince, status, urls, onStateChange }) {
  const [stateError, setStateError] = useState(null);
  return (
    <section className="regie-entete" aria-label="Direct" data-testid="regie-header">
      <div className="regie-entete__faits">
        <span className={`etat ${STATE_BADGES[state] || "etat--neutre"}`} data-testid="regie-state" data-state={state}>
          {STATE_LABELS[state] || state}
        </span>
        <LiveClock state={state} liveSince={liveSince} />
        <span className="regie-entete__presents" data-testid="regie-present" aria-live="polite">{presentLabel(status)}</span>
        <a className="btn btn--secondary regie-bouton regie-entete__salle" href={urls.stage} target="_blank" rel="noopener" data-testid="regie-open-room">
          Ouvrir la salle ↗
        </a>
      </div>
      {/* Seul sur sa ligne, calé à gauche : armé, le bouton s'élargit vers
          la droite et « Annuler » vient après lui — le second clic d'un
          double-clic retombe sur le bouton, jamais sur « Annuler ». */}
      <div className="regie-entete__actions">
        <StateButton
          state={state}
          stateUrl={urls.state}
          goLiveUrl={urls.goLive}
          endUrl={urls.end}
          resumeUrl={urls.resume}
          onStateChange={onStateChange}
          onError={setStateError}
        />
      </div>
      {stateError ? <p className="regie-alerte" role="alert" data-testid="state-error">{stateError}</p> : null}
    </section>
  );
}

function RegieSection({ title, testid, children }) {
  return (
    <section className="regie-bloc" aria-label={title} data-testid={testid}>
      <h2 className="regie-bloc__titre">{title}</h2>
      {children}
    </section>
  );
}

// Mise en avant : la régie demande, le serveur écrit les métadonnées de la
// salle, la relecture du statut montre ce que la salle diffuse. Un partage
// d'écran passe d'office en plein cadre chez tous (priorité de la salle,
// spotlight.jsx) : il est signalé, pas commandé.
function SpotlightConsole({ spotlightUrl, status, onChanged }) {
  const control = useSpotlightControl(spotlightUrl);
  const request = useCallback(async (identity) => {
    await control.request(identity);
    onChanged();
  }, [control, onChanged]);

  if (!status) return <p className="regie-note">Lecture de la salle…</p>;
  if (status.room === "unavailable") {
    return <p className="regie-alerte" role="alert">Salle LiveKit injoignable : la mise en avant est indisponible.</p>;
  }
  const onStage = status.on_stage || [];
  const featured = status.spotlight;
  const sharing = onStage.filter((person) => person.screen_share);
  const featuredAbsent = featured && !onStage.some((person) => person.identity === featured);

  return (
    <div className="regie-scene" data-testid="regie-spotlight">
      {sharing.length > 0 ? (
        <p className="regie-note regie-note--info" data-testid="regie-screen-share">
          Partage d'écran de {sharing.map((person) => person.name).join(", ")} : en plein cadre pour tous tant qu'il dure.
        </p>
      ) : null}
      {onStage.length === 0 ? (
        <p className="regie-note">Personne sur scène.</p>
      ) : (
        <ul className="regie-liste">
          {onStage.map((person) => {
            const isFeatured = person.identity === featured;
            const label = isFeatured ? "Retirer la mise en avant" : "Mettre en avant";
            return (
              <li className="regie-liste__item" key={person.identity} data-testid="regie-stage-person" data-featured={isFeatured}>
                <span className="regie-liste__nom">{person.name}</span>
                {isFeatured ? <span className="regie-liste__meta">en avant</span> : null}
                {person.awaiting_consent ? (
                  <span className="regie-liste__meta" data-testid="regie-awaiting-consent">en attente d'accord</span>
                ) : person.camera ? null : <span className="regie-liste__meta">caméra coupée</span>}
                <button
                  type="button"
                  className="btn btn--secondary regie-bouton"
                  aria-pressed={isFeatured}
                  aria-label={`${label} : ${person.name}`}
                  disabled={control.pending}
                  onClick={() => request(isFeatured ? null : person.identity)}
                  data-testid="regie-spotlight-toggle"
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {featuredAbsent ? <p className="regie-note">La personne mise en avant a quitté la scène : la salle affiche la mosaïque.</p> : null}
      <button
        type="button"
        className="btn btn--secondary regie-bouton"
        disabled={!featured || control.pending}
        onClick={() => request(null)}
        data-testid="regie-spotlight-clear"
      >
        Revenir à la mosaïque
      </button>
      {control.error ? <p className="regie-alerte" role="alert" data-testid="spotlight-error">{control.error}</p> : null}
    </div>
  );
}

const ROLE_NAMES = { speaker: "intervenant", viewer: "spectateur" };

const MODERATION_REFUSALS = {
  invalid_identity: "personne non modérable depuis la régie",
  not_found: "personne inconnue de ce direct",
  not_in_room: "la personne n'est plus dans la salle, rien n'a été fait",
  not_live: "hors direct, seuls le propriétaire et l'animateur du projet modèrent",
  room_update_failed: "la salle LiveKit n'a pas répondu",
};

// Refus définitifs : relancer ne changerait rien.
const FINAL_REFUSALS = new Set(["invalid_identity", "not_found", "not_in_room", "not_live"]);

// Commande de modération (POST <participantsUrl>/<identité>/<mute|remove>) ;
// le serveur revérifie le rôle d'animation et la cible à chaque appel.
async function moderate(url) {
  let response;
  try {
    response = await fetch(url, {
      method: "POST", credentials: "same-origin", cache: "no-store", redirect: "manual",
      headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
    });
  } catch (_networkError) {
    throw new Error("réseau indisponible");
  }
  if (response.type === "opaqueredirect") throw new Error("session animateur expirée, reconnectez-vous");
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const error = new Error(MODERATION_REFUSALS[payload?.error] || `serveur ${response.status}`);
    error.final = FINAL_REFUSALS.has(payload?.error);
    throw error;
  }
}

// Une personne modérable. « Retirer » se confirme en deux temps : la personne
// quitte le direct ; ce que le retrait garantit et ne garantit pas est écrit
// sous la liste (docs/moderation.md).
function ParticipantRow({ person, participantsUrl, onChanged, onError }) {
  const [pending, setPending] = useState(false);
  const { armed, confirmed, disarm, controlRef } = useTwoStepConfirm();
  const baseUrl = `${participantsUrl}/${encodeURIComponent(person.identity)}`;

  const run = useCallback(async (action, failure, finalFailure = failure) => {
    setPending(true);
    onError(null);
    try {
      await moderate(`${baseUrl}/${action}`);
    } catch (err) {
      onError(`${err.final ? finalFailure : failure} : ${err.message}.`);
    } finally {
      setPending(false);
      onChanged();
    }
  }, [baseUrl, onChanged, onError]);

  const mute = () => run("mute", `Micro de ${person.name} non coupé`);
  const remove = () => {
    if (pending || !confirmed()) return;
    run("remove", `Retrait de ${person.name} non confirmé, relancez « Retirer »`, `${person.name} non retiré`);
  };

  return (
    <li className="regie-liste__item" data-testid="regie-participant" data-identity={person.identity}>
      <span className="regie-liste__nom">{person.name}</span>
      <span className="regie-liste__meta">{ROLE_NAMES[person.role] || person.role}</span>
      {person.microphone ? <span className="regie-liste__meta">micro ouvert</span> : null}
      <span className="regie-moderation" ref={controlRef}>
        <button
          type="button"
          className="btn btn--secondary regie-bouton"
          aria-label={`Couper le micro : ${person.name}`}
          disabled={pending || !person.microphone}
          onClick={mute}
          data-testid="regie-participant-mute"
        >
          Couper le micro
        </button>
        <button
          type="button"
          className="btn btn--secondary regie-bouton regie-bouton--critique"
          aria-label={armed ? `Confirmer le retrait de ${person.name}` : `Retirer ${person.name} du direct`}
          data-armed={armed ? "true" : "false"}
          disabled={pending}
          onClick={remove}
          data-testid="regie-participant-remove"
        >
          {armed ? "Confirmer le retrait\u00a0?" : "Retirer"}
        </button>
        {armed ? (
          <button type="button" className="btn btn--secondary regie-bouton" onClick={disarm} data-testid="regie-participant-cancel">
            Annuler
          </button>
        ) : null}
      </span>
    </li>
  );
}

// Intervenants et spectateurs présents (jamais l'animation), lus par le
// serveur dans LiveKit (/regie/status, `participants`).
function ParticipantsConsole({ participantsUrl, status, onChanged }) {
  const [error, setError] = useState(null);
  if (!status) return <p className="regie-note">Lecture de la salle…</p>;
  if (status.room === "unavailable") {
    return <p className="regie-alerte" role="alert">Salle LiveKit injoignable : la modération est indisponible.</p>;
  }
  const people = status.participants || [];

  return (
    <div className="regie-scene" data-testid="regie-participants">
      {people.length === 0 ? (
        <p className="regie-note">Aucun intervenant ni spectateur dans la salle.</p>
      ) : (
        <ul className="regie-liste">
          {people.map((person) => (
            <ParticipantRow key={person.identity} person={person} participantsUrl={participantsUrl}
              onChanged={onChanged} onError={setError} />
          ))}
        </ul>
      )}
      <p className="regie-note">
        Couper le micro : la personne peut le rallumer. Retirer : elle quitte le direct et ne peut plus revenir depuis
        le même navigateur, avec les mêmes cookies, ni avec la même adresse. Retirer ne l'empêche pas de revenir
        depuis un autre navigateur ou en navigation privée, avec une autre adresse ou par le lien intervenant partagé
        tant qu'il n'est pas régénéré (page du webinaire).
      </p>
      {error ? <p className="regie-alerte" role="alert" data-testid="participants-error">{error}</p> : null}
    </div>
  );
}

const ENQUEUE_REFUSALS = {
  404: "Message introuvable : rechargez la page.",
  422: "Message effacé : il ne peut pas entrer dans la file.",
};

// Ajout manuel d'un message à la file des questions (QaItemsController#create,
// source « manual »). 201 = nouvelle question, 200 = message déjà dans la
// file (le serveur ne crée pas de doublon).
async function enqueueMessage(qaItemsUrl, message) {
  let response;
  try {
    response = await fetch(qaItemsUrl, {
      method: "POST", credentials: "same-origin", cache: "no-store", redirect: "manual",
      headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ message_id: message.id }),
    });
  } catch (_networkError) {
    throw new Error("Réseau indisponible.");
  }
  if (response.type === "opaqueredirect") throw new Error("Session animateur expirée, reconnectez-vous.");
  if (!response.ok) throw new Error(ENQUEUE_REFUSALS[response.status] || `Ajout à la file refusé (${response.status}).`);
  return response.status === 201 ? "Ajouté à la file des questions." : "Ce message est déjà dans la file des questions.";
}

function Discussion({ messagesUrl, moderationUrl, qaItemsUrl, onEnqueued }) {
  const chat = useChat({ messagesUrl, speakerToken: null });
  const onEnqueue = useCallback(async (message) => {
    const notice = await enqueueMessage(qaItemsUrl, message);
    onEnqueued();
    return notice;
  }, [qaItemsUrl, onEnqueued]);
  return <DiscussionPanel chat={chat} moderationUrl={moderationUrl} onEnqueue={onEnqueue} visible />;
}

function Regie({ initialState, initialLiveSince, urls }) {
  const { status, error: statusError, refresh } = useRegieStatus(urls.status);
  const [state, setState] = useState(initialState || "draft");
  const serverState = status?.state;
  // Le serveur reste juge : un changement venu d'ailleurs (autre animateur,
  // fin automatique) s'impose à l'affichage.
  useEffect(() => {
    if (serverState) setState(serverState);
  }, [serverState]);
  const liveSince = status ? status.live_since : initialLiveSince;

  const handleStateChange = useCallback((next) => {
    setState(next);
    refresh();
  }, [refresh]);

  // Un ajout depuis la discussion relit aussitôt la file des questions.
  const [qaRefresh, setQaRefresh] = useState(0);
  const handleEnqueued = useCallback(() => setQaRefresh((count) => count + 1), []);

  return (
    <div className="regie__grille">
      <RegieHeader state={state} liveSince={liveSince} status={status} urls={urls} onStateChange={handleStateChange} />
      {statusError ? (
        <p className="regie-alerte" role="alert" data-testid="regie-status-error">
          État de la régie illisible : {statusError}
        </p>
      ) : null}
      <div className="regie__colonne">
        <RegieSection title="Enregistrement" testid="regie-recording">
          <RecordingControl statusUrl={urls.recordingStatus} startUrl={urls.recordingStart} />
        </RegieSection>
        <RegieSection title="Mise en avant" testid="regie-spotlight-section">
          <SpotlightConsole spotlightUrl={urls.spotlight} status={status} onChanged={refresh} />
        </RegieSection>
        <RegieSection title="Participants" testid="regie-participants-section">
          <ParticipantsConsole participantsUrl={urls.participants} status={status} onChanged={refresh} />
        </RegieSection>
        <RegieSection title="Mains levées" testid="regie-hands">
          <HandRaiseHost listUrl={urls.handRaises} />
        </RegieSection>
        <RegieSection title="Sondages" testid="regie-polls">
          <PollConsole pollsUrl={urls.polls} presentCount={status?.present ?? null} />
        </RegieSection>
      </div>
      <div className="regie__colonne">
        <RegieSection title="Questions du public" testid="regie-qa">
          <QaConsole itemsUrl={urls.qaItems} refreshSignal={qaRefresh} />
        </RegieSection>
        <RegieSection title="Discussion" testid="regie-discussion">
          <Discussion messagesUrl={urls.messages} moderationUrl={urls.messageModeration}
            qaItemsUrl={urls.qaItems} onEnqueued={handleEnqueued} />
        </RegieSection>
      </div>
    </div>
  );
}

export function mountRegie() {
  const el = document.getElementById(MOUNT_ID);
  if (!el) return;
  const data = el.dataset;
  if (!data.slug || !data.statusUrl) {
    // Marqueur explicite d'un data-attr manquant (fail-loud).
    el.textContent = "regie: data-slug ou data-status-url manquant";
    return;
  }
  const urls = {
    status: data.statusUrl,
    state: data.stateUrl,
    goLive: data.goLiveUrl,
    end: data.endUrl,
    resume: data.resumeUrl,
    recordingStatus: data.recordingStatusUrl,
    recordingStart: data.recordingStartUrl,
    handRaises: data.handRaisesUrl,
    spotlight: data.spotlightUrl,
    participants: data.participantsUrl,
    messages: data.messagesUrl,
    messageModeration: data.messageModerationUrl,
    polls: data.pollsUrl,
    qaItems: data.qaItemsUrl,
    stage: data.stageUrl,
  };
  createRoot(el).render(
    <Regie initialState={data.state} initialLiveSince={data.liveSince || null} urls={urls} />,
  );
}
